import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import {
  Package, Plus, Pencil, Trash2, Loader2, RefreshCw, DollarSign, Users, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import adminService from '../../services/adminService';

const FEATURE_FLAGS = [
  { key: 'canViewComplianceReports', label: 'Compliance Reports' },
  { key: 'canDownloadOperatorExcel', label: 'Download Operator Excel' },
  { key: 'canAccessMultiState', label: 'Multi-State Access' },
  { key: 'canAddMultipleUsers', label: 'Multiple Users' },
  { key: 'canUploadDocuments', label: 'Upload Documents' },
  { key: 'canUseAuditTrail', label: 'Audit Trail' },
  { key: 'canUseScheduler', label: 'Scheduler' },
  { key: 'canAccessAIInsights', label: 'AI Insights' },
  { key: 'canAccessLabIntegration', label: 'Lab Integration' },
  { key: 'canUseBlockchainCertificates', label: 'Blockchain Certificates' },
];

const EMPTY_PLAN = {
  name: '',
  description: '',
  price: '',
  durationInMonths: '',
  numberOfOperators: '',
  numberOfStates: '',
  canViewComplianceReports: false,
  canDownloadOperatorExcel: false,
  canAccessMultiState: false,
  canAddMultipleUsers: false,
  canUploadDocuments: false,
  canUseAuditTrail: false,
  canUseScheduler: false,
  canAccessAIInsights: false,
  canAccessLabIntegration: false,
  canUseBlockchainCertificates: false,
};

function PlanFormDialog({ open, onOpenChange, plan, onSave, isSaving, title, description }) {
  const [form, setForm] = useState(EMPTY_PLAN);

  useEffect(() => {
    if (open) setForm(plan ?? EMPTY_PLAN);
  }, [open, plan]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      price: parseFloat(form.price) || 0,
      durationInMonths: parseInt(form.durationInMonths) || 0,
      numberOfOperators: parseInt(form.numberOfOperators) || 0,
      numberOfStates: parseInt(form.numberOfStates) || 0,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1">
              <Label>Plan Name *</Label>
              <Input value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Professional" />
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Description</Label>
              <Input value={form.description} onChange={e => set('description', e.target.value)} placeholder="Short description" />
            </div>
            <div className="space-y-1">
              <Label>Price ($) *</Label>
              <Input type="number" step="0.01" min="0" value={form.price} onChange={e => set('price', e.target.value)} required placeholder="99.00" />
            </div>
            <div className="space-y-1">
              <Label>Duration (months) *</Label>
              <Input type="number" min="1" value={form.durationInMonths} onChange={e => set('durationInMonths', e.target.value)} required placeholder="12" />
            </div>
            <div className="space-y-1">
              <Label>Max Operators</Label>
              <Input type="number" min="0" value={form.numberOfOperators} onChange={e => set('numberOfOperators', e.target.value)} placeholder="10" />
            </div>
            <div className="space-y-1">
              <Label>Max States</Label>
              <Input type="number" min="0" value={form.numberOfStates} onChange={e => set('numberOfStates', e.target.value)} placeholder="5" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold">Feature Flags</Label>
            <div className="grid grid-cols-1 gap-2 border rounded-md p-3">
              {FEATURE_FLAGS.map(f => (
                <div key={f.key} className="flex items-center justify-between">
                  <Label className="text-sm font-normal cursor-pointer" htmlFor={`flag-${f.key}`}>{f.label}</Label>
                  <Switch
                    id={`flag-${f.key}`}
                    checked={!!form[f.key]}
                    onCheckedChange={v => set(f.key, v)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="submit" className="flex-1" disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              {plan ? 'Save Changes' : 'Create Plan'}
            </Button>
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSubscriptionPlansPage() {
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editPlan, setEditPlan] = useState(null);

  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getPlans();
      setPlans(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load plans');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleCreate = async (dto) => {
    setIsSaving(true);
    try {
      await adminService.createPlan(dto);
      toast.success('Plan created');
      setIsCreateOpen(false);
      fetchPlans();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to create plan');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (dto) => {
    setIsSaving(true);
    try {
      await adminService.updatePlan(editPlan.subscriptionPlanId ?? editPlan.id, dto);
      toast.success('Plan updated');
      setEditPlan(null);
      fetchPlans();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to update plan');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"? This cannot be undone.`)) return;
    try {
      await adminService.deletePlan(plan.subscriptionPlanId ?? plan.id);
      toast.success('Plan deleted');
      fetchPlans();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to delete plan');
    }
  };

  const enabledFlags = (plan) => FEATURE_FLAGS.filter(f => plan[f.key]).map(f => f.label);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Subscription Plans</h1>
            <p className="text-muted-foreground mt-1">Manage available subscription plans and their feature flags</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchPlans}>
              <RefreshCw className="h-4 w-4 mr-2" />Refresh
            </Button>
            <Button onClick={() => setIsCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />New Plan
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <Package className="h-16 w-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg">No subscription plans yet. Create your first plan.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map(plan => {
              const flags = enabledFlags(plan);
              return (
                <Card key={plan.subscriptionPlanId ?? plan.id} className="flex flex-col">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{plan.name}</CardTitle>
                        {plan.description && (
                          <CardDescription className="mt-1">{plan.description}</CardDescription>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => setEditPlan({ ...plan })}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(plan)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 space-y-4">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1 font-semibold text-lg">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        {plan.price?.toFixed(2)}
                        <span className="text-sm font-normal text-muted-foreground">/{plan.durationInMonths}mo</span>
                      </span>
                    </div>
                    <div className="flex gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />{plan.numberOfOperators ?? '∞'} operators
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />{plan.numberOfStates ?? '∞'} states
                      </span>
                    </div>
                    {flags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {flags.map(label => (
                          <Badge key={label} variant="secondary" className="text-xs">{label}</Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <PlanFormDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        plan={null}
        onSave={handleCreate}
        isSaving={isSaving}
        title="Create Subscription Plan"
        description="Define a new subscription plan with features and pricing."
      />

      <PlanFormDialog
        open={!!editPlan}
        onOpenChange={v => { if (!v) setEditPlan(null); }}
        plan={editPlan}
        onSave={handleUpdate}
        isSaving={isSaving}
        title={`Edit Plan: ${editPlan?.name}`}
        description="Update plan details and feature flags."
      />
    </DashboardLayout>
  );
}
